import { useState } from 'react';
import { EDGES, ENTITIES, FLOW, type EntitySummary, type FlowItem } from './entity-summaries-data.js';
import { TONE, type ArchitectureTone } from './ArchitectureGraph.js';
import { TONE_CLASS } from './entity-tone-classes.js';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const FLOW_BY_LABEL = new Map(FLOW.map((item) => [item.label, item]));

const LANES: Array<{ label: string; tone: ArchitectureTone; x: number; y: number; width: number; height: number }> = [
  { label: 'Workspace planning boundary', tone: 'cloud', x: 32, y: 42, width: 916, height: 218 },
  { label: 'Accountable execution', tone: 'backend', x: 212, y: 202, width: 616, height: 120 },
  { label: 'Durable outputs and adoption', tone: 'database', x: 212, y: 482, width: 616, height: 118 },
];

const LEGEND: Array<{ label: string; tone: ArchitectureTone }> = [
  { label: 'Workspace', tone: 'frontend' },
  { label: 'Execution', tone: 'backend' },
  { label: 'Attention', tone: 'security' },
  { label: 'Outputs', tone: 'database' },
  { label: 'Actors', tone: 'cloud' },
  { label: 'Adoption', tone: 'external' },
];

function entityForMapLabel(label: string): EntitySummary | undefined {
  return ENTITIES.find((entity) => entity.name === label || entity.name.startsWith(label));
}

function tooltipPosition(item: FlowItem) {
  const width = 340;
  const height = 140;
  return {
    x: Math.max(18, Math.min(item.x - 70, 980 - width - 18)),
    y: item.y < 180 ? item.y + 74 : item.y - height - 16,
    width,
    height,
  };
}

function MapNodeTooltip({ item, entity }: { item: FlowItem; entity: EntitySummary }) {
  const pos = tooltipPosition(item);
  const tone = TONE_CLASS[entity.tone];
  return (
    <foreignObject x={pos.x} y={pos.y} width={pos.width} height={pos.height} className="pointer-events-none">
      <div
        className={`h-full overflow-hidden rounded-md border bg-surface px-4 py-3 text-left shadow-2xl ${tone.border}`}
        style={{ fontFamily: MONO }}
      >
        <div className={`text-[10px] font-semibold uppercase tracking-widest ${tone.text}`}>{entity.name}</div>
        <div className="mt-2 text-[12px] leading-5 text-body">{entity.summary}</div>
      </div>
    </foreignObject>
  );
}

/** The entity relationship map: plain SVG painted with theme variables, so it follows light and dark. */
export function EntityMap() {
  const [activeNode, setActiveNode] = useState<FlowItem | null>(null);
  const activeEntity = activeNode ? entityForMapLabel(activeNode.label) : undefined;

  return (
    <svg viewBox="0 0 980 660" role="img" aria-label="Conflui entity relationship map" className="h-auto w-full">
      <defs>
        <marker id="entity-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="var(--color-faint)" />
        </marker>
      </defs>

      {LANES.map((lane) => (
        <g key={lane.label}>
          <rect
            x={lane.x}
            y={lane.y}
            width={lane.width}
            height={lane.height}
            rx="14"
            fill="none"
            stroke={TONE[lane.tone].stroke}
            strokeDasharray="8 5"
            opacity="0.55"
          />
          <text x={lane.x + 20} y={lane.y + 28} fill={TONE[lane.tone].stroke} fontSize="11" fontFamily={MONO}>
            {lane.label}
          </text>
        </g>
      ))}

      {EDGES.map(([from, to]) => {
        const source = FLOW_BY_LABEL.get(from);
        const target = FLOW_BY_LABEL.get(to);
        if (!source || !target) return null;
        return (
          <line
            key={`${from}-${to}`}
            x1={source.x + 120}
            y1={source.y + 30}
            x2={target.x}
            y2={target.y + 30}
            stroke="var(--color-faint)"
            strokeWidth="1.5"
            markerEnd="url(#entity-arrow)"
            opacity="0.75"
          />
        );
      })}

      {FLOW.map((item) => {
        const paint = TONE[item.tone];
        const entity = entityForMapLabel(item.label);
        return (
          <g
            key={item.label}
            tabIndex={0}
            role="img"
            aria-label={entity ? `${item.label}: ${entity.summary}` : item.label}
            className="cursor-help outline-hidden"
            onMouseEnter={() => setActiveNode(item)}
            onMouseLeave={() => setActiveNode((current) => (current?.label === item.label ? null : current))}
            onFocus={() => setActiveNode(item)}
            onBlur={() => setActiveNode((current) => (current?.label === item.label ? null : current))}
          >
            <rect x={item.x - 2} y={item.y - 2} width="124" height="64" rx="7" fill="var(--color-surface)" />
            <rect
              x={item.x}
              y={item.y}
              width="120"
              height="60"
              rx="6"
              fill={paint.fill}
              stroke={paint.stroke}
              strokeWidth="1.5"
            />
            <text
              x={item.x + 12}
              y={item.y + 27}
              fill="var(--color-heading)"
              fontSize="12"
              fontWeight="700"
              fontFamily={MONO}
            >
              {item.label}
            </text>
            <text x={item.x + 12} y={item.y + 43} fill={paint.text} fontSize="8" fontFamily={MONO}>
              {item.tone}
            </text>
          </g>
        );
      })}

      <g>
        {LEGEND.map((entry, index) => {
          const x = 70 + index * 120;
          return (
            <g key={entry.label}>
              <rect x={x} y="624" width="12" height="12" rx="3" fill={TONE[entry.tone].stroke} opacity="0.75" />
              <text x={x + 20} y="634" fill="var(--color-body)" fontSize="9" fontFamily={MONO}>
                {entry.label}
              </text>
            </g>
          );
        })}
      </g>

      {activeNode && activeEntity && <MapNodeTooltip item={activeNode} entity={activeEntity} />}
    </svg>
  );
}
